import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import createMarkup from 'create-markup';
import { graphql } from 'gatsby';

// hoc
import withI18next from '../../i18n/withI18next';

// hooks
import useSmallScreen from "../../hooks/useSmallScreen";

// components
import Layout from '../../components/layout';
import StickyWrapper from '../../components/StickyWrapper/StickyWrapper';
import ArticleContentWrapper from '../../components/ArticleContentWrapper/ArticleContentWrapper';
import ArticleContent from '../../components/ArticleContent/ArticleContent';
import BackgroundWithLinesHalf from '../../components/Background/BackgroundWithLinesHalf';
import LocationIcon from '../../icons/LocationIcon';
import TimeIcon from '../../icons/TimeIcon';
import CalendarIcon from '../../icons/CalendarIcon';
import SectionWhiteFirst from '../../components/PageSections/SectionWhiteFirst';
import SectionWhiteHalfPage from '../../components/PageSections/SectionWhiteHalfPage';
import Format from '../../components/Format/Format';
import { Script } from '../../components/Script/Script';

// helpers
import { languageSelectorLocalization } from '../../helpers/localeHelpers';

// styles
import './event-page.scss';

const EventPage = ({data, location}) => {
  const {smallScreen} = useSmallScreen();
  const layoutRef = useRef();
  const { contentfulEventPage, localizations } = data;
  const {
    title,
    body,
    eventImage,
    imageName,
    imageTitle,
    // date,
    eventDate,
    evenStopTime,
    location: eventLocation,
    metaTitle,
    metaDescription,
    keywords,
    ogImage,
    script
  } = contentfulEventPage;

  const languageSelectorInformation = languageSelectorLocalization('/nyheter', localizations);

  const markup =
    body && body.childMarkdownRemark && body.childMarkdownRemark.html
      ? createMarkup(body.childMarkdownRemark.html)
      : { __html: '' };
      
  const imageUrl = eventImage.file.url;
  
  const updateLogoColor = color => {
    layoutRef.current.handleLogoColor(color);
  };

  return (
    <Layout
      title={metaTitle}
      description={metaDescription}
      keywords={keywords}
      ogImage={ogImage && ogImage.file.url}
      location={location}
      ref={layoutRef}
      languageSelectorInformation={languageSelectorInformation}
      halfPage
    >
      <div className="event-page">
        <div className="event-page__container">
          <div className="--left">
            <StickyWrapper>
              <div className="event-page__sticky-content">
                <div className="event-page__group-item">
                  <CalendarIcon />
                  <span className="date"><Format type="date">{eventDate}</Format></span>
                </div>
                <div className="event-page__group-item">
                  <TimeIcon />
                  <span>
                    Kl <Format type="time">{eventDate}</Format> - <Format type="time">{evenStopTime}</Format>
                  </span>
                </div>
                <div className="event-page__group-item">
                  <LocationIcon />
                  <span>{eventLocation}</span>
                </div>
              </div>
            </StickyWrapper>
          </div>
          <div className="--right">
            <BackgroundWithLinesHalf theme={'white'}>
              {smallScreen && (
                <SectionWhiteFirst logoColor={updateLogoColor} />
              )}
              {!smallScreen && (
                <SectionWhiteHalfPage logoColor={updateLogoColor} />
              )}
              <ArticleContentWrapper>
                <ArticleContent>
                  <h1 className="event-page__event-title">{title}</h1>

                  <figure className="event-page__author-figure">
                    <img
                      alt={imageName}
                      src={imageUrl + '?fm=png&w=60&h=60&fit=scale'}
                    />
                    <figcaption>
                      <span>{imageName}</span>
                      <span>{imageTitle}</span>
                    </figcaption>
                  </figure>
                  <div
                    className="event-page__body-text"
                    dangerouslySetInnerHTML={markup}
                  />
                </ArticleContent>
              </ArticleContentWrapper>
            </BackgroundWithLinesHalf>
          </div>
        </div>
      </div>
      {script &&
        <Script script={script.script}/>
      }
    </Layout>
  );
}

export default withI18next()(EventPage);

EventPage.defaultProps = {
  data: {
    contentfulEventPage: {
      title: '',
      cases: []
    }
  }
};

EventPage.propTypes = {
  data: PropTypes.object
};

export const pageQuery = graphql`
  query eventPageQuery($trueSlug: String!, $locale: String!) {
    contentfulEventPage(slug: { eq: $trueSlug }, language: {eq: $locale}) {
      metaTitle
      metaDescription
      keywords
      ogImage{
        file{
          url
        }
      }
      script{
        script
      }
      title
      type
      body {
        id
        childMarkdownRemark {
          id
          html
        }
      }
      eventImage {
        id
        file {
          url
          fileName
          contentType
        }
      }
      imageName
      imageTitle
      date
      eventDate
      evenStopTime
      location
    }
    localizations: allContentfulEventPage(filter: {slug: { eq: $trueSlug }}){
     edges {
      node {
        slug
        language
      }
     }
    }
  }
`;
